import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, FileCheck2, Landmark, Banknote, Users, FileText, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Calculator } from "../components/Calculator";
import { PlanCards } from "../components/PlanCards";
import { Faq } from "../components/Faq";
import { Testimonials } from "../components/Testimonials";
import { SITE, HOME_FAQ, CONTRACTOR, money, orgSchema, faqSchema, offerCatalogSchema, url } from "../data/site";
import { REGIONS, STATES, stateSlug, sortedAbbrs } from "../data/states";

const includes = [
  [Landmark, "Tax filing, done for you", "Federal Forms 941 and 940, state withholding and unemployment returns, and local taxes, filed and paid on time."],
  [Banknote, "Direct deposit", "Pay employees and contractors by direct deposit on the schedule you choose, with unlimited payroll runs."],
  [MapPin, "Every state, one price", "Hire in a new state and we register the accounts. There is no per-state surcharge on any plan."],
  [FileText, "Year-end forms", "W-2s for employees and 1099-NECs for contractors, prepared, filed and delivered electronically."],
  [FileCheck2, "New-hire reporting", "Each new employee is reported to the right state directory, and we collect W-4s and I-9 details at onboarding."],
  [Users, "Employee portal", "Your team views pay stubs, updates direct deposit and downloads W-2s without emailing you."],
];

const steps = [
  ["Send your last payroll register", "Share your most recent payroll report and your federal and state tax account numbers. Nothing else is needed to start."],
  ["We set up your accounts", "We import year-to-date wages, set up each employee and register any state accounts you are missing."],
  ["Check the first run together", "Before your first ABSLpay payroll, a CPA compares it line by line with your previous provider."],
  ["Go live", "Your team is paid on schedule and we file the quarter's returns, including the periods before you switched."],
];

const industries = [
  ["Restaurants and bars", "Tip reporting, tip pooling, overtime for hourly staff and the FICA tip credit on Form 8846."],
  ["IT and consulting firms", "H-1B wage requirements, remote staff in several states and consultants paid as 1099 contractors."],
  ["Construction and trades", "Crews across job sites, certified payroll reports, and clear employee-versus-contractor classification."],
  ["Real estate and property management", "On-site staff, commission payouts and payroll across property-owning entities."],
  ["Medical and dental practices", "Salaried and hourly clinical staff, bonuses, and payroll split across multiple locations."],
  ["Owner-managed S corporations", "Reasonable compensation for owners, shareholder health insurance on the W-2, and quarterly planning."],
];

export default function Home() {
  const { scrollY } = useScroll();
  const calcY = useTransform(scrollY, [0, 500], [0, -40]);

  const websiteSchema = {
    "@context": "https://schema.org", "@type": "WebSite", "@id": url("/#website"),
    url: url("/"), name: SITE.brand, publisher: { "@id": url("/#org") },
  };

  return (
    <>
      <Seo
        title="Payroll Services for Small Business in All 50 States | ABSLpay"
        description="CPA-reviewed payroll from $39/mo + $5 per person. Direct deposit, tax filing, W-2s and multi-state payroll included in all 50 states and DC."
        path="/"
        jsonLd={[orgSchema(), websiteSchema, offerCatalogSchema(), faqSchema(HOME_FAQ)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#ECFDF5_0%,transparent_55%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-emerald-800"
            >
              <ShieldCheck size={14} /> CPA-reviewed &middot; All 50 states + DC
            </motion.p>
            <MaskedLines
              lines={["Payroll for", "all 50 states,", "reviewed by CPAs", "before it runs."]}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
            >
              ABSLpay pays your team, files federal, state and local payroll taxes, and handles W-2s and new-hire
              reporting in all 50 states and DC. Plans start at $39 a month plus $5 per person, with multi-state
              payroll included.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-get-quote-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-7 py-3.5 hover:bg-emerald-700 transition-colors"
              >
                Get a free quote <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-compare-plans-btn"
                className="inline-flex items-center rounded-full border border-slate-300 bg-white font-bold px-7 py-3.5 text-slate-800 hover:border-slate-900 transition-colors"
              >
                Compare plans
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              className="mt-7 text-sm text-slate-500"
            >
              Run by an experienced CPA who serves several business clients.
            </motion.p>
          </div>
          <motion.div style={{ y: calcY }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="flex lg:justify-end">
            <Calculator />
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-slate-200 bg-white py-4 overflow-hidden" aria-hidden="true">
        <div className="marquee-track gap-10">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center gap-10">
              {sortedAbbrs.map((a) => (
                <span key={`${dup}-${a}`} className="flex items-center gap-10 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Payroll in {STATES[a][0]} <span className="text-emerald-500">&#9670;</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Includes */}
      <section className="bg-white" id="included">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Full service</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">What every plan includes</h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Full-service payroll means we calculate, pay and file. You approve the payroll; we handle everything after that.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {includes.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-slate-200 bg-[#FAFAFA] p-6 transition-colors hover:border-emerald-300">
                  <Icon size={22} className="text-emerald-600" />
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Pricing</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              Published prices, lower than the big names
            </h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              No setup fee, no annual contract and no charge per payroll run. You pay a monthly base fee plus a fee for
              each person you pay that month.
            </p>
          </Reveal>
          <div className="mt-12"><PlanCards /></div>
          <Reveal>
            <p className="mt-8 text-sm text-slate-500">
              Contractor-only businesses: {money(CONTRACTOR.base)}/month + {money(CONTRACTOR.per)} per contractor.{" "}
              <Link to="/pricing#compare" className="font-semibold text-emerald-700 hover:underline">
                See the full comparison with Gusto and ADP
              </Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Switching */}
      <section className="bg-white" id="switch">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Migration</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Switching takes about two weeks</h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Moving from Gusto, ADP, Paychex or QuickBooks Payroll is free. We do the setup work.
            </p>
          </Reveal>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <li className="h-full rounded-2xl border border-slate-200 p-6">
                  <span className="font-mono-num text-sm font-bold text-emerald-600">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Industries — dark band */}
      <section className="relative bg-slate-950 grain">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-400">Who we serve</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Built for the businesses we already do taxes for
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur-sm transition-colors hover:border-emerald-700">
                  <h3 className="font-display text-lg font-bold text-white">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* States */}
      <section id="states">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Coverage</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              Payroll in all 50 states and Washington, DC
            </h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Choose your state for local tax requirements, unemployment insurance and paid-leave programs we handle there.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {Object.entries(REGIONS).map(([region, members], i) => (
              <Reveal key={region} delay={i * 0.08}>
                <h3 className="font-display text-base font-bold text-slate-900 border-b border-slate-200 pb-3">{region}</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {[...members].sort((a, b) => STATES[a][0].localeCompare(STATES[b][0])).map((a) => (
                    <li key={a}>
                      <Link to={`/payroll-services/${stateSlug(a)}`} className="text-slate-600 hover:text-emerald-700 transition-colors">
                        {STATES[a][0]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <section id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Common questions</h2>
          </Reveal>
          <div className="mt-10"><Faq qas={HOME_FAQ} testPrefix="home-faq" /></div>
          <Reveal>
            <p className="mt-8 text-sm text-slate-500">
              Looking for something specific?{" "}
              <Link to="/faq" data-testid="home-faq-search-link" className="font-semibold text-emerald-700 hover:underline">
                Search every FAQ — pricing, switching and all 50 states
              </Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">See your price in one call</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
              Tell us your headcount and the states you pay in. We will send a written quote the same business day.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="cta-get-quote-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-7 py-3.5 hover:bg-emerald-700 transition-colors">
                Get a free quote <ArrowRight size={17} />
              </Link>
              <a href={`tel:${SITE.phone}`} data-testid="cta-call-btn" className="inline-flex items-center rounded-full border border-slate-300 bg-white font-bold px-7 py-3.5 text-slate-800 hover:border-slate-900 transition-colors">
                Call {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
